import { buttonClass } from "./design";
import { DefaultTemplateLink } from "./templateLink";
import type { ReactNode } from "react";
import type { PageLink, ResolvedTemplateDesign, SiteInfo, TemplateLinkComponent } from "./types";

export function SiteHeader({
    site,
    pages,
    design,
    LinkComponent = DefaultTemplateLink,
    headerActions,
}: {
    site: SiteInfo | null;
    pages: PageLink[];
    design: ResolvedTemplateDesign;
    LinkComponent?: TemplateLinkComponent;
    headerActions?: ReactNode;
}) {
    const centered = design.components.header === "centered";
    const minimal = design.components.header === "minimal";

    return (
        <header className="border-b border-[var(--site-border)] bg-[var(--site-background)]/95">
            <div
                className={`mx-auto flex max-w-[var(--site-container)] gap-4 px-5 py-5 sm:px-6 ${
                    centered ? "flex-col items-center text-center" : "items-center justify-between"
                }`}
            >
                <LinkComponent href="/" className="font-[var(--site-heading-font)] text-xl font-bold text-[var(--site-text)]">
                    {site?.name ?? "Webdo site"}
                </LinkComponent>
                <nav className="flex flex-wrap items-center justify-center gap-4 text-sm text-[var(--site-muted)]">
                    {pages.slice(0, 6).map((page) => (
                        <LinkComponent key={page.slug} href={`/${page.slug}`} className="hover:text-[var(--site-primary)]">
                            {page.title}
                        </LinkComponent>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    {headerActions}
                    {!minimal ? (
                        <LinkComponent href="/kontakt" className={`px-4 py-2 text-sm font-semibold ${buttonClass(design.components.buttons)}`}>
                            Kontakt
                        </LinkComponent>
                    ) : null}
                </div>
            </div>
        </header>
    );
}

export function SiteFooter({
    site,
    pages,
    design,
    LinkComponent = DefaultTemplateLink,
}: {
    site: SiteInfo | null;
    pages: PageLink[];
    design: ResolvedTemplateDesign;
    LinkComponent?: TemplateLinkComponent;
}) {
    if (design.components.footer === "simple") {
        return (
            <footer className="border-t border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-8 text-sm text-[var(--site-muted)]">
                <div className="mx-auto max-w-[var(--site-container)]">
                    © 2026 {site?.name ?? "Webdo site"}
                </div>
            </footer>
        );
    }

    if (design.components.footer === "contact") {
        return (
            <footer className="border-t border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-10">
                <div className="mx-auto grid max-w-[var(--site-container)] gap-6 sm:grid-cols-[1.2fr_0.8fr]">
                    <div>
                        <p className="font-[var(--site-heading-font)] text-lg font-bold text-[var(--site-text)]">
                            {site?.name ?? "Webdo site"}
                        </p>
                        <p className="mt-2 max-w-xl text-sm leading-6 text-[var(--site-muted)]">
                        Ozvete se nam a spolecne pripravime dalsi krok pro vas web.
                    </p>
                </div>
                <nav className="flex flex-wrap gap-3 text-sm text-[var(--site-muted)] sm:justify-end">
                    {pages.slice(0, 5).map((page) => (
                        <LinkComponent key={page.slug} href={`/${page.slug}`} className="hover:text-[var(--site-primary)]">
                            {page.title}
                        </LinkComponent>
                    ))}
                </nav>
            </div>
            </footer>
        );
    }

    return (
        <footer className="border-t border-[var(--site-border)] bg-[var(--site-surface)] px-5 py-10">
            <div className="mx-auto grid max-w-[var(--site-container)] gap-8 sm:grid-cols-3">
                <div>
                    <p className="font-[var(--site-heading-font)] text-lg font-bold text-[var(--site-text)]">
                        {site?.name ?? "Webdo site"}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-[var(--site-muted)]">
                        Moderni web postaveny na obsahu z editoru a designu vybrane sablony.
                    </p>
                </div>
                <nav className="space-y-2 text-sm text-[var(--site-muted)]">
                    {pages.slice(0, 4).map((page) => (
                        <LinkComponent key={page.slug} href={`/${page.slug}`} className="block hover:text-[var(--site-primary)]">
                            {page.title}
                        </LinkComponent>
                    ))}
                </nav>
                <div className="text-sm leading-6 text-[var(--site-muted)]">
                    <p>Obsah, formulare a menu zustavaji systemove.</p>
                    <p>Vzhled ridi sablona a admin customizace.</p>
                </div>
            </div>
        </footer>
    );
}
